import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBalanceScale,
  faBoxesStacked,
  faCalculator,
  faCashRegister,
  faChartLine,
  faCheckCircle,
  faCircleNodes,
  faFileInvoiceDollar,
  faShieldHalved,
  faStore,
  faTruckRampBox,
} from "@fortawesome/free-solid-svg-icons";
import "./BillingSoftware.css";
import "./BestBusinessSoftware.css";
import { PRICING_DATA, RENTAL_PRICING, formatPriceForRegion } from "./pricingConfig";

const path = "/best-business-software";
const title = "Best Business Software Buyer Guide | Auditus-iERP";
const description =
  "Find the right business software with Auditus-iERP. Compare ERP, billing, accounting, inventory, POS, GST, pricing, demo, and business-fit criteria.";

const moduleCards = [
  [faCircleNodes, "ERP", "Connected business management across daily operations.", "/erp-software"],
  [faFileInvoiceDollar, "Billing", "GST-ready invoices, products, customers, payments, and reports.", "/billing-software"],
  [faCalculator, "Accounting", "Ledgers, vouchers, expenses, payments, and financial reports.", "/accounting-software"],
  [faBoxesStacked, "Inventory", "Products, stock tracking, barcode movement, warehouses, batches, and item reports.", "/inventory-management-software"],
  [faCashRegister, "POS", "Retail checkout workflows with billing, products, payments, barcode support, and inventory.", "/pos-software"],
  [faBalanceScale, "GST", "GST-ready invoices, tax calculation workflows, GSTIN, HSN/SAC, e-invoice, e-way bill, and GST reports.", "/gst-software"],
  [faTruckRampBox, "Sales & Purchases", "Sales transactions, purchase entries, customer records, supplier records, and tracking.", "/features-solutions"],
  [faChartLine, "Reports", "Sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports.", "/features-solutions"],
];

const decisionCriteria = [
  ["Billing", "Can the software create invoices quickly and keep product, customer, payment, and sales details organized?"],
  ["Accounting", "Can ledgers, vouchers, expenses, payments, receivables, payables, and reports work from the same transaction data?"],
  ["Inventory", "Can product records, stock tracking, purchases, sales, barcode movement, batches, warehouses, and reports stay connected?"],
  ["POS", "For retail, does it support fast checkout, barcode workflows, payment handling, customers, and stock visibility?"],
  ["GST", "Does it support GST-ready invoices, tax calculation workflows, GSTIN, HSN/SAC, GST reports, and related compliance workflows?"],
  ["Reporting", "Can owners review sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports?"],
  ["Business fit", "Does the software match your business type, staff workflow, support needs, and long-term growth plans?"],
  ["Pricing value", "Does the price make sense for the number of users, modules, support, and operational time saved?"],
];

const buyerSections = [
  {
    icon: faFileInvoiceDollar,
    title: "Looking for Billing Software?",
    intro:
      "If you are comparing the best billing software, best billing software in India, or billing software for small business, look for fast billing, invoice generation, GST billing, product management, customer management, inventory connection, payment records, and reports.",
    fit:
      "Auditus-iERP includes GST-ready billing, invoice creation, product selection, customer details, payment recording, barcode-supported workflows, inventory connection, and sales reports.",
    link: "/billing-software",
    linkText: "Explore billing software",
  },
  {
    icon: faCalculator,
    title: "Looking for Accounting Software?",
    intro:
      "When evaluating accounting software, check ledger management, transactions, expenses, payments, receivables, payables, reports, billing integration, and inventory integration.",
    fit:
      "Auditus-iERP supports ledgers, vouchers, sales, purchases, expenses, payments, GST-related records, financial reports, and connected billing and inventory workflows.",
    link: "/accounting-software",
    linkText: "Explore accounting software",
  },
  {
    icon: faBoxesStacked,
    title: "Looking for Inventory Management Software?",
    intro:
      "For inventory software, buyers should evaluate product management, stock tracking, purchase integration, sales integration, reports, barcode support, warehouses, batches, and billing connection.",
    fit:
      "Auditus-iERP includes product and stock management, barcode movement, warehouses, batches, reorder levels, stock tracking, purchase and sales connection, and item-wise reports.",
    link: "/inventory-management-software",
    linkText: "Explore inventory management",
  },
  {
    icon: faCashRegister,
    title: "Looking for POS Software?",
    intro:
      "For POS software, retail buyers should check checkout speed, billing, product management, barcode support, inventory integration, payment handling, and reporting.",
    fit:
      "Auditus-iERP includes POS billing for retail workflows with product selection, barcode support, payment recording, customers, inventory connection, and sales reports.",
    link: "/pos-software",
    linkText: "Explore POS software",
  },
  {
    icon: faBalanceScale,
    title: "Looking for GST Software?",
    intro:
      "For GST billing software, evaluate GST invoice creation, tax calculation workflows, GST-related records, GST reports, and billing/accounting integration.",
    fit:
      "Auditus-iERP supports GST-ready invoices, tax calculation workflows, GSTIN details, HSN/SAC handling, GST reports, e-invoice workflows, and e-way bill workflows.",
    link: "/gst-software",
    linkText: "Explore GST software",
  },
];

const businessTypes = [
  ["Supermarkets", "Fast billing, POS, barcode workflows, products, inventory, payments, GST-ready invoices, and reports."],
  ["Retail businesses", "Retail billing, product management, customer records, payment handling, stock visibility, and sales reporting."],
  ["Textile businesses", "Billing, product and stock management, purchases, accounting, GST workflows, customers, suppliers, and reports."],
  ["Grocery businesses", "Product billing, inventory visibility, purchases, sales tracking, customer records, and business reports."],
  ["Wholesale businesses", "Sales, purchases, inventory, customer and supplier records, payments, accounting, and reports."],
  ["Distributors", "Billing, inventory, purchases, customer and supplier records, payments, and business reporting."],
  ["Small businesses", "A connected way to manage invoices, accounts, products, payments, GST workflows, and reports as work grows."],
];

const selectorRows = [
  ["Fast retail billing", "POS + Billing", "/pos-software"],
  ["Financial management", "Accounting", "/accounting-software"],
  ["Stock control", "Inventory", "/inventory-management-software"],
  ["GST billing", "GST + Billing", "/gst-software"],
  ["Complete business management", "ERP", "/erp-software"],
];

const comparisonRows = [
  ["Billing", "Often managed in a separate billing tool.", "GST-ready billing, invoices, products, customers, payments, and sales records."],
  ["Accounting", "May require separate entry or reconciliation.", "Ledgers, vouchers, payments, expenses, and reports connected to transactions."],
  ["Inventory", "Stock updates may need manual syncing.", "Products, stock tracking, purchases, sales, barcode movement, batches, and warehouses."],
  ["POS", "Retail checkout may sit outside accounts and stock.", "POS billing with barcode support, payments, customers, inventory, and sales reports."],
  ["GST", "GST information may be prepared from separate records.", "GST-ready invoices, GSTIN, HSN/SAC, e-invoice, e-way bill, and GST reports."],
  ["Sales", "Sales records can be isolated from stock and accounts.", "Sales transactions connect with customers, billing, inventory, payments, and reports."],
  ["Purchases", "Supplier purchases may be tracked separately.", "Purchase entries connect with suppliers, stock, payments, accounts, and reports."],
  ["Customers", "Customer history may be split across tools.", "Customer records, ledgers, balances, transaction history, and payment references."],
  ["Suppliers", "Supplier records may need separate follow-up.", "Supplier records, purchase activity, balances, payment references, and reports."],
  ["Reports", "Reports can take more manual preparation.", "Sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports."],
  ["Data connection", "More duplicate data entry between tools.", "Shared business information across connected modules."],
];

const indiaLicensePlans = PRICING_DATA.plans.map((plan) => [
  plan.name,
  `${formatPriceForRegion(plan.indiaPrice, "India")} + 18% GST`,
  "Perpetual license for India.",
]);

const lowestRentalPrice = Math.min(...Object.values(RENTAL_PRICING).map((plan) => plan.price));
const pricingPlans = [
  ...indiaLicensePlans,
  [
    "Rental Plan",
    `From ${formatPriceForRegion(lowestRentalPrice, "India")}/month + 18% GST`,
    "India rental options are available for Single User, Dual User, and Multi User.",
  ],
];

const screenshots = [
  ["/images/screenshots/dashboard.png", "Auditus-iERP business management software dashboard", "Business dashboard"],
  ["/images/screenshots/invoice.png", "Auditus-iERP billing software screen", "Billing and POS screen"],
  ["/images/screenshots/reports.png", "Auditus-iERP accounting and business reporting screen", "Reports screen"],
  ["/images/gstdashboard.PNG", "Auditus-iERP GST software dashboard", "GST dashboard"],
];

const faqs = [
  ["What should I look for when choosing business software?", "Look for billing, accounting, inventory, POS, GST, reporting, ease of use, integration, scalability, support, pricing value, and fit for your business workflow."],
  ["What is the best billing software for a small business?", "The right billing software depends on your business needs. Small businesses should look for fast invoice creation, GST billing, products, customers, payments, inventory connection, and reports. Auditus-iERP supports these billing workflows."],
  ["What is the best accounting software for a business?", "A good accounting choice should handle ledgers, vouchers, transactions, expenses, payments, receivables, payables, and reports. Auditus-iERP connects accounting with billing, inventory, purchases, sales, and GST workflows."],
  ["What is the best inventory management software?", "The right inventory software should support product management, stock tracking, purchase and sales integration, barcode workflows, warehouses, batches, reorder levels, and reports. Auditus-iERP includes these inventory workflows."],
  ["What is the best POS software for retail?", "Retail POS software should support fast checkout, billing, products, barcode support, payments, inventory connection, customers, and reports. Auditus-iERP supports these POS workflows."],
  ["What is the best GST billing software?", "The right GST billing software should support GST-ready invoices, tax calculation workflows, GSTIN details, HSN/SAC, GST reports, and accounting or billing integration. Auditus-iERP supports these GST workflows."],
  ["Can one software handle billing and accounting?", "Yes. Auditus-iERP includes billing and accounting workflows so invoices, payments, ledgers, vouchers, financial reports, and GST-related records can work together."],
  ["Can business software manage inventory and billing together?", "Yes. Auditus-iERP connects inventory and billing through products, stock tracking, purchases, sales, barcode movement, and item-wise reporting."],
  ["Is Auditus-iERP suitable for small businesses?", "Yes. Auditus-iERP can help small businesses manage billing, accounts, stock, GST workflows, payments, customers, suppliers, and reports in one connected system."],
  ["Can Auditus-iERP be used by retail businesses?", "Yes. Retail businesses can use POS billing, barcode workflows, products, customers, payments, inventory, GST workflows, and reports."],
  ["Can Auditus-iERP be used by supermarkets?", "Yes. Supermarkets can use fast billing, POS, barcode workflows, product records, inventory, purchases, sales tracking, payments, and reports."],
  ["Can Auditus-iERP be used by textile businesses?", "Yes. Textile businesses can use billing, products, inventory, purchases, accounting, GST workflows, customers, suppliers, and reports."],
  ["How can I compare Auditus-iERP with other business software?", "Compare your required modules, reporting needs, user count, pricing, support, ease of use, and whether billing, accounts, inventory, POS, GST, and reports need to work together."],
  ["How can I get an Auditus-iERP demo?", "Use the Book a Demo button to open the demo request form and share your business details."],
  ["Does Auditus-iERP have pricing information?", "Yes. The existing pricing page lists India perpetual license plans, India rental options, and South Asia or Global subscription pricing. Use View Pricing for the current page details."],
];

function setMeta(name, content, attribute = "name") {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

export default function BestBusinessSoftware() {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", path, "property");
    setMeta("og:image", "/images/screenshots/dashboard.png", "property");
    setLink("canonical", path);

    const schema = document.createElement("script");
    schema.id = "best-business-software-schema";
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: "Best Business Software", item: path },
          ],
        },
        {
          "@type": "SoftwareApplication",
          name: "Auditus-iERP",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Windows, Android",
          url: path,
          image: "/images/screenshots/dashboard.png",
          description,
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "INR",
            lowPrice: "3000",
            highPrice: "108000",
            offerCount: "4",
          },
          featureList: moduleCards.map((item) => item[1]),
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
      ],
    });
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      schema.remove();
    };
  }, []);

  return (
    <main className="billing-page bbs-page">
      <section className="bbs-hero" aria-labelledby="best-business-software-title">
        <div className="billing-container bbs-hero-grid">
          <div>
            <nav className="billing-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Best Business Software</span>
            </nav>
            <p className="billing-kicker">
              <FontAwesomeIcon icon={faCircleNodes} aria-hidden="true" />
              Buyer intent guide
            </p>
            <h1 id="best-business-software-title">Find the Right Business Software for Your Business</h1>
            <p className="billing-lead">
              Auditus-iERP combines ERP, billing, accounting, inventory, POS, GST,
              and business management in one connected platform for businesses that
              want to compare software, check pricing, and request a demo.
            </p>
            <div className="billing-actions">
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="billing-btn billing-btn-secondary">
                Get Started
              </Link>
              <Link to="/Pricing" className="billing-btn bbs-btn-pricing">
                View Pricing
              </Link>
            </div>
            <div className="bbs-hero-proof" aria-label="Decision support">
              <span>No rankings claimed</span>
              <span>Real product screens</span>
              <span>Published pricing page</span>
            </div>
          </div>
          <div className="billing-window bbs-hero-window">
            <div className="billing-window-bar">
              <span>Auditus-iERP - Buyer Overview</span>
              <i />
            </div>
            <img
              src="/images/screenshots/dashboard.png"
              alt="Auditus-iERP business management software dashboard"
              width="920"
              height="560"
              loading="eager"
            />
            <div className="billing-window-foot">
              <span>ERP</span>
              <span>Billing</span>
              <span>Accounts</span>
              <span>Reports</span>
            </div>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="decision-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Buyer checklist</p>
            <h2 id="decision-title">What Should You Look for in Business Software?</h2>
            <p>
              Choosing business software is not only about finding a familiar name.
              The better question is whether the software fits your billing, accounts,
              stock, tax, reporting, staff, support, and pricing needs.
            </p>
          </div>
          <div className="bbs-decision-grid">
            {decisionCriteria.map(([name, text]) => (
              <article key={name}>
                <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                <h3>{name}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section bbs-dark" aria-labelledby="integrated-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Integrated platform</p>
            <h2 id="integrated-title">One Platform Instead of Multiple Disconnected Systems</h2>
          </div>
          <div className="billing-copy">
            <p>
              Separate billing software, accounting software, and inventory software
              can work for isolated tasks, but buyers should consider duplicate data
              entry, reporting effort, data consistency, and whether staff need one
              shared view of business activity.
            </p>
            <p>
              An integrated business management platform like Auditus-iERP can connect
              billing, accounts, stock, purchases, sales, POS, GST, payments, customers,
              suppliers, and reports so daily work is easier to follow.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="glance-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Auditus-iERP at a glance</p>
            <h2 id="glance-title">Explore the Capabilities of Auditus-iERP</h2>
            <p>
              These verified modules help buyers understand whether Auditus-iERP fits
              their business needs before booking a demo.
            </p>
          </div>
          <div className="bbs-module-grid">
            {moduleCards.map(([icon, name, text, link]) => (
              <Link to={link} className="bbs-module-card" key={name}>
                <span className="billing-feature-icon">
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </span>
                <strong>{name}</strong>
                <p>{text}</p>
              </Link>
            ))}
          </div>
          <div className="bbs-inline-cta">
            <Link to="/features-solutions" className="billing-btn billing-btn-secondary">
              Explore the Software
            </Link>
            <Link to="/Demo" className="billing-btn billing-btn-primary">
              Talk to Our Team <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="billing-section bbs-fit" aria-labelledby="fit-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Product fit</p>
            <h2 id="fit-title">Why Auditus-iERP May Be Right for Your Business</h2>
          </div>
          <div className="billing-copy">
            <p>
              Auditus-iERP may be a good fit if your business wants integrated billing,
              accounting, inventory, POS, GST-related functionality, business reports,
              and centralized information instead of maintaining separate systems.
            </p>
            <p>
              It is especially relevant for buyers comparing billing software with GST,
              billing software with inventory, accounting software with inventory, POS
              software for retail, and ERP software for small business workflows.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="buyer-sections-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Search intent match</p>
            <h2 id="buyer-sections-title">Find the Right Software Category</h2>
            <p>
              Use these sections to compare what you need with the verified Auditus-iERP
              capabilities available in the product ecosystem.
            </p>
          </div>
          <div className="bbs-buyer-stack">
            {buyerSections.map((item) => (
              <article key={item.title}>
                <div className="bbs-buyer-icon">
                  <FontAwesomeIcon icon={item.icon} aria-hidden="true" />
                </div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.intro}</p>
                  <p>{item.fit}</p>
                  <Link to={item.link}>
                    {item.linkText} <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section bbs-businesses" aria-labelledby="businesses-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Business type fit</p>
            <h2 id="businesses-title">Which Businesses Can Use Auditus-iERP?</h2>
            <p>
              Auditus-iERP is relevant to businesses that need connected billing, stock,
              accounts, GST workflows, POS, purchases, sales, customers, suppliers,
              payments, and reporting.
            </p>
          </div>
          <div className="bbs-business-grid">
            {businessTypes.map(([name, text]) => (
              <article key={name}>
                <FontAwesomeIcon icon={faStore} aria-hidden="true" />
                <h3>{name}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
          <div className="bbs-inline-cta">
            <Link to="/industries" className="billing-btn billing-btn-secondary">
              View Industry Solutions
            </Link>
            <Link to="/Demo" className="billing-btn billing-btn-primary">
              Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="selection-guide-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Selection guide</p>
            <h2 id="selection-guide-title">Business Software Selection Guide</h2>
            <p>
              Start with the problem your team needs to solve first. Auditus-iERP can
              combine these areas when your business needs a connected platform.
            </p>
          </div>
          <div className="bbs-selector">
            {selectorRows.map(([need, solution, link]) => (
              <Link to={link} key={need}>
                <span>If you need</span>
                <strong>{need}</strong>
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                <em>{solution}</em>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section bbs-comparison-section" aria-labelledby="comparison-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Feature comparison</p>
            <h2 id="comparison-title">Separate Software vs Integrated Auditus-iERP</h2>
            <p>
              This comparison is not a competitor ranking. It shows practical buying
              considerations when choosing between isolated tools and one connected system.
            </p>
          </div>
          <div className="bbs-comparison-table" role="table" aria-label="Separate software compared with integrated Auditus-iERP">
            <div className="bbs-table-row bbs-table-head" role="row">
              <span role="columnheader">Category</span>
              <span role="columnheader">Separate Software</span>
              <span role="columnheader">Integrated Auditus-iERP</span>
            </div>
            {comparisonRows.map(([category, separate, integrated]) => (
              <div className="bbs-table-row" role="row" key={category}>
                <strong role="cell">{category}</strong>
                <p role="cell">{separate}</p>
                <p role="cell">{integrated}</p>
              </div>
            ))}
          </div>
          <div className="bbs-inline-cta">
            <Link to="/Demo" className="billing-btn billing-btn-primary">
              Compare in a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="screens-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Real product screenshots</p>
            <h2 id="screens-title">See the Product Before You Decide</h2>
            <p>
              These are real Auditus-iERP screenshot assets already present in the
              project, reused with accurate alt text.
            </p>
          </div>
          <div className="bbs-screenshot-grid">
            {screenshots.map(([src, alt, label]) => (
              <figure key={src}>
                <img src={src} alt={alt} loading="lazy" width="640" height="390" />
                <figcaption>{label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section bbs-pricing" aria-labelledby="pricing-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Pricing and value</p>
            <h2 id="pricing-title">Choose Software That Fits Your Business Needs</h2>
            <p>
              The project includes a real pricing page, so the buyer guide shows the
              current published India pricing summary and links to the full pricing route.
            </p>
          </div>
          <div className="bbs-pricing-grid">
            {pricingPlans.map(([name, price, note]) => (
              <article key={name}>
                <h3>{name}</h3>
                <strong>{price}</strong>
                <p>{note}</p>
              </article>
            ))}
          </div>
          <div className="bbs-inline-cta">
            <Link to="/Pricing" className="billing-btn billing-btn-secondary">
              View Pricing
            </Link>
            <Link to="/Demo" className="billing-btn billing-btn-primary">
              Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="billing-section bbs-trust" aria-labelledby="trust-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Trust signals</p>
            <h2 id="trust-title">Evaluate Auditus-iERP with Real Product Information</h2>
          </div>
          <div className="billing-copy">
            <p>
              This page uses genuine project signals only: real product routes, real
              screenshot assets, a working demo request route, a support/contact route,
              and pricing information already present in the project.
            </p>
            <p>
              No fake rankings, reviews, awards, customer logos, certifications, or
              countdown offers are used.
            </p>
            <div className="billing-actions">
              <Link to="/Support" className="billing-btn billing-btn-secondary">
                Contact Us
              </Link>
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="billing-section billing-faq-section" aria-labelledby="faq-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Buyer FAQ</p>
            <h2 id="faq-title">Business Software Buying Questions</h2>
          </div>
          <div className="billing-faq-list">
            {faqs.map(([q, a], index) => {
              const open = openFaq === index;
              return (
                <article className={`billing-faq-item ${open ? "is-open" : ""}`} key={q}>
                  <h3>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`best-business-software-faq-${index}`}
                      onClick={() => setOpenFaq(open ? null : index)}
                    >
                      <span className="billing-faq-symbol" aria-hidden="true">{open ? "-" : "+"}</span>
                      {q}
                    </button>
                  </h3>
                  <div
                    id={`best-business-software-faq-${index}`}
                    className="billing-faq-answer"
                    aria-hidden={!open}
                  >
                    <p>{a}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="billing-final">
        <div className="billing-container">
          <div className="billing-final-panel">
            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
            <p className="billing-final-kicker">Buyer-ready next step</p>
            <h2>Ready to Find the Right Software for Your Business?</h2>
            <p>
              Explore Auditus-iERP with your real billing, accounting, inventory, POS,
              GST, reporting, user, and pricing requirements in mind.
            </p>
            <div className="billing-actions">
              <Link to="/Demo" className="billing-btn billing-btn-primary">
                Book a Demo <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Trial" className="billing-btn billing-btn-secondary">
                Get Started
              </Link>
              <Link to="/Support" className="billing-btn billing-btn-secondary">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
